import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AcceptCguInput,
  CguAcceptanceResult,
  CguChangelogView,
  CguVersionView,
  HasSignedView,
  LegalClient,
} from '@/api/legal-client';
import { useLegalClient } from '@/api/legal-client-context';

// =============================================================================
// use-legal.ts — Hooks TanStack Query des CGU fournisseur (via le double).
// =============================================================================
// Lecture de la version courante + du statut de signature (T-06) en useQuery ;
// signature en useMutation (effet de bord serveur : acceptation immuable,
// DE.CguAcceptanceRecorded, PDF). Le client effectif vient du LegalClientProvider
// (doublable). Aucune règle métier ici : l'erreur API remonte telle quelle.
// L'intentionnalité par lecture (scroll complet) est une garde UI (composant),
// pas une règle dupliquée du serveur ; `contentDisplayed` est posé par l'appelant.

const LEGAL_KEY = ['legal'] as const;

/** GET version courante des CGU à signer (RÉFÉRENCE PDF : bucket/objectKey/sha256). */
export function useCurrentCgu(): UseQueryResult<CguVersionView, Error> {
  const client: LegalClient = useLegalClient();
  return useQuery<CguVersionView, Error>({
    queryKey: [...LEGAL_KEY, 'current-cgu'],
    queryFn: () => client.currentCgu(),
  });
}

/** GET résumé des évolutions de la CGU courante (DBU §3.1 ; texte brut rendu échappé). */
export function useCurrentCguChangelog(): UseQueryResult<CguChangelogView, Error> {
  const client: LegalClient = useLegalClient();
  return useQuery<CguChangelogView, Error>({
    queryKey: [...LEGAL_KEY, 'current-cgu-changelog'],
    queryFn: () => client.currentCguChangelog(),
  });
}

/**
 * GET les OCTETS du PDF CGU courant (S7) — consommés par le renderer pdf.js (DocumentViewer).
 * Séparé de `useCurrentCgu` (référence/métadonnées) : le binaire est volumineux et n'a pas la
 * même cadence de revalidation. `staleTime: Infinity` — un PDF write-once est IMMUABLE : le
 * discriminant (`versionKey`, p.ex. le sha256 write-once ou le cguVersionId) figé dans la clé
 * garantit qu'aucun refetch n'a lieu tant que la version ne change pas. La requête n'est armée
 * qu'une fois ce discriminant connu (gate `enabled`).
 */
export function useCurrentCguPdf(
  versionKey: string | undefined,
): UseQueryResult<ArrayBuffer, Error> {
  const client: LegalClient = useLegalClient();
  return useQuery<ArrayBuffer, Error>({
    queryKey: [...LEGAL_KEY, 'current-cgu-pdf', versionKey ?? null],
    queryFn: () => client.currentCguPdf(),
    enabled: versionKey !== undefined && versionKey.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * GET statut de signature du fournisseur courant (T-06, relatif à la version courante).
 * `options.enabled` (défaut `true`) permet de GATER l'appel : l'orchestration du login
 * (magic-link-screen) ne sonde `hasSigned` qu'APRÈS ouverture de session (sinon 401). Les
 * consommateurs existants (bandeau CGU) l'appellent sans option → comportement inchangé.
 */
export function useHasSignedCgu(
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<HasSignedView, Error> {
  const client: LegalClient = useLegalClient();
  return useQuery<HasSignedView, Error>({
    queryKey: [...LEGAL_KEY, 'has-signed'],
    queryFn: () => client.hasSigned(),
    enabled: options.enabled ?? true,
  });
}

/** POST signature des CGU — acceptation immuable (legally-binding côté API). */
export function useAcceptCgu(): UseMutationResult<CguAcceptanceResult, Error, AcceptCguInput> {
  const client: LegalClient = useLegalClient();
  const qc = useQueryClient();
  return useMutation<CguAcceptanceResult, Error, AcceptCguInput>({
    mutationKey: [...LEGAL_KEY, 'accept-cgu'],
    mutationFn: (input: AcceptCguInput) => client.acceptCgu(input),
    // BUGFIX (2026-09-11) : sans ceci, le cache `has-signed` restait à `false` après signature.
    // Le SupplierCguGate (lecture SYNCHRONE du cache) re-redirigeait alors vers /cgu au 1er
    // passage — d'où « rien ne se passe la 1re fois, il faut recommencer ». On pose la valeur
    // signée IMMÉDIATEMENT (setQueryData, pas de course avec la navigation) PUIS on invalide
    // pour réconcilier avec l'autorité serveur.
    onSuccess: (_result, input) => {
      qc.setQueryData<HasSignedView>([...LEGAL_KEY, 'has-signed'], (old) =>
        old === undefined
          ? { currentVersionId: input.cguVersionId, hasSigned: true, signedVersionId: input.cguVersionId }
          : { ...old, hasSigned: true, signedVersionId: input.cguVersionId },
      );
      void qc.invalidateQueries({ queryKey: [...LEGAL_KEY, 'has-signed'] });
    },
  });
}
